import i18n from "../i18n";
import React, { useMemo, useState } from "react";
import { Tabs } from "@mantine/core";
import { useAuthUser } from "react-auth-kit";
import { useLocation } from "react-router-dom";
import { PageList, ENUM_FORM_DISPLAY } from "../components/pageList";
import { IconEye, IconCircleCheck } from "@tabler/icons";
import ActionCellRender from "../components/actionCellRender";
import McaPageStats from "../components/mca/McaPageStats";
import { mcaRequestColumns } from "../components/mca/mcaTableHelpers";
import { getMcaRequestPermissions } from "../components/mca/mcaRequestPermissions";
import { getEntityLabel } from "../data/mcaEntities";
import { useTabNavigate } from "../hooks/useTabNavigate";
import { openEntityEditTab } from "../utils/openEntityEditTab";

const apiEntity = "mcaRequest";

const formSchema = {
  title: "MCA Request",
  apiEntity,
  display: {
    mode: ENUM_FORM_DISPLAY.MODAL,
    size: "sm",
  },
  initialValues: {},
  layout: {
    fields: [],
  },
};

const McaRequestTabs = ({ tab, setTab }) => (
  <Tabs value={tab} onTabChange={setTab} mb="md">
    <Tabs.List>
      <Tabs.Tab value="pending">{i18n.t("My Pending Approvals")}</Tabs.Tab>
      <Tabs.Tab value="submitted">{i18n.t("My Submissions")}</Tabs.Tab>
      <Tabs.Tab value="all">{i18n.t("All Requests")}</Tabs.Tab>
    </Tabs.List>
  </Tabs>
);

const McaRequest = () => {
  const tabNav = useTabNavigate();
  const location = useLocation();
  const auth = useAuthUser();
  const session = auth();
  const [tab, setTab] = useState("pending");

  const preQuery = useMemo(() => {
    if (tab === "pending") return { _mcaTab: "pending" };
    if (tab === "submitted") return { makerOnly: true };
    return {};
  }, [tab]);

  const emptyState = useMemo(() => {
    if (tab === "pending") {
      return { message: "No pending approval" };
    }
    if (tab === "submitted") {
      return { message: "No submissions" };
    }
    return null;
  }, [tab]);

  const openDetail = (data) => {
    const label = data.code ? `MCA - ${data.code}` : "MCA Request";
    tabNav.open({
      pathname: `/mca/requests/${data._id}`,
      label,
    });
  };

  const openEntity = (data) => {
    if (!data?.targetEntityId) return;
    openEntityEditTab(tabNav, {
      entity: data.targetEntity,
      id: data.targetEntityId,
      label: data.targetEntityCode ?? getEntityLabel(data.targetEntity),
      userRole: session?.userRole,
      currentPathname: location.pathname,
    });
  };

  const tableSchema = useMemo(
    () => ({
      searchableFields: [
        "code",
        "targetEntity",
        "targetEntityCode",
        "action",
        "searchText",
      ],
      columns: [
        ...mcaRequestColumns(),
        {
          cellRender: ActionCellRender,
          isAction: true,
          actions: [
            {
              name: "approve",
              label: "Review & approve",
              icon: IconCircleCheck,
              visible: (data) =>
                getMcaRequestPermissions(data, session).canApprove,
            },
            { name: "view", label: "View Entity", icon: IconEye, visible: (data) => Boolean(data?.targetEntityId) },
          ],
        },
      ],
      onActionBtnClickCustom: async ({ action, data }) => {
        if (action === "approve") openDetail(data);
        if (action === "view") openEntity(data);
      },
      onRowClick: openDetail,
    }),
    [session, tabNav, location.pathname]
  );

  return (
    <PageList
      apiEntity={apiEntity}
      title="Approval Requests"
      formSchema={formSchema}
      hideActionButton
      showAddButton={false}
      preQuery={preQuery}
      stats={{ component: McaPageStats, props: { tab } }}
      header={{
        component: McaRequestTabs,
        props: { tab, setTab },
      }}
      tableSchema={tableSchema}
      emptyState={emptyState}
    />
  );
};

export default McaRequest;
